import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, makeStyles, Tab, Typography } from '@material-ui/core';
import { TabContext, TabList, TabPanel } from '@material-ui/lab';
import { useHistory, useParams } from 'react-router-dom';
import Loading from 'components/Loading';
import { fonts } from 'theme';
import NoPaperPageContent from 'components/Generic/NoPaperPageContent';
import { useUserById } from 'hooks';
import { MenuProvider } from 'menuContext';
import { goBack } from 'utils/history';
import { Routes } from 'routes';
import { notify } from 'components/Notifier';
import { deleteAllSortingCenterUserRolesByAdminSortingCenter } from 'services/apiOperations';
import { transformObjectId } from 'services/apiPlatformTransformer';
import NewDialog from 'components/NewDialog';
import { DialogWidth, User } from 'types';
import AdministrationLayout from './Layout';
import { UserInformation } from './Users/UserInformation';
import SortingCenterUserRolesList from './Users/SortingCenterUserRolesList';

const useStyles = makeStyles(theme => ({
  page: {
    width: '100%',
    maxWidth: '942px',
  },
  label: {
    fontWeight: 600,
    marginRight: '16px',

    '&:after': {
      content: '" :"',
    },
  },
  tabPanel: {
    padding: '24px 0',
    fontFamily: fonts.secondary,
    width: '100%',
    fontWeight: 600,
  },
  tabTitle: {
    marginLeft: '20px',
    marginRight: '20px',
  },
  content: {
    marginBottom: '32px',
    marginRight: '32px',

    '& p': {
      fontSize: 16,
      lineHeight: '24px',
      textAlign: 'left',
      verticalAlign: 'top',
    },
    '& p span': {
      color: '#FF9500',
    },
  },
}));

enum TabLabels {
  SortingCenters = 'SortingCenters',
  Information = 'Information',
}

const AdministrationUserShow: React.FC = () => {
  const history = useHistory();
  const classes = useStyles();
  const { t } = useTranslation('translation', { keyPrefix: 'administration.users.show' });
  const { t: td } = useTranslation('translation', { keyPrefix: 'administration.users' });
  const { userId } = useParams<{ userId: string }>();
  const user = useUserById(userId);
  const [selectedTab, setSelectedTab] = useState(TabLabels.SortingCenters);
  const [dialogDeleteOpen, setDialogDeleteOpen] = useState(false);

  const handleDialogDelete = async (user: User) => {
    try {
      await deleteAllSortingCenterUserRolesByAdminSortingCenter(transformObjectId(user));
      notify({
        message: td('dialog.delete.success'),
        severity: 'success',
      });
      goBack('/administration/users', history);
    } catch (e) {
      notify({
        message: t('errorGeneric'),
        severity: 'error',
      });
    }
  };

  return (
    <>
      <MenuProvider>
        <AdministrationLayout>
          <NoPaperPageContent
            deleteButtonAction={() => setDialogDeleteOpen(true)}
            title={t('title')}
            backAction={() => goBack(Routes.AdministrationUsers, history)}
          >
            {user === null ? (
              <Loading />
            ) : (
              user && (
                <Box className={classes.page}>
                  <NewDialog
                    closable={true}
                    isOpen={dialogDeleteOpen}
                    onClose={() => setDialogDeleteOpen(false)}
                    width={DialogWidth.LARGE_FORM}
                    title={td('dialog.delete.title')}
                    renderContent={() => (
                      <div className={classes.content}>
                        <Typography>{td('dialog.delete.content')}</Typography>
                      </div>
                    )}
                    genericFooter={true}
                    closeButtonLabelFooter={td('dialog.delete.cancel')}
                    continueButtonLabelFooter={td('dialog.delete.continue')}
                    handleDialog={() => handleDialogDelete(user)}
                  />
                  <Divider />
                  <Box display="flex" paddingTop="26px" paddingBottom="26px">
                    <Box marginRight="200px">
                      <Typography>
                        <span className={classes.label}>{t('lastName')}</span>
                        {user.lastName}
                      </Typography>
                    </Box>
                    <Typography>
                      <span className={classes.label}>{t('firstName')}</span>
                      {user.firstName}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box display="flex" paddingTop="26px" paddingBottom="52px" marginRight="200px">
                    <Typography>
                      <span className={classes.label}>{t('email')}</span>
                      {user.email}
                    </Typography>
                  </Box>
                  <TabContext value={selectedTab}>
                    <TabList
                      value={selectedTab}
                      onChange={(e, newValue) => {
                        setSelectedTab(newValue);
                      }}
                      indicatorColor="primary"
                      textColor="primary"
                    >
                      <Tab label={t('tabs.sortingCenters')} value={TabLabels.SortingCenters} />
                      <Tab className={classes.tabTitle} label={t('tabs.information')} value={TabLabels.Information} />
                    </TabList>
                    <Divider />
                    <TabPanel value={TabLabels.SortingCenters} className={classes.tabPanel}>
                      <SortingCenterUserRolesList user={user} />
                    </TabPanel>
                    <TabPanel value={TabLabels.Information} className={classes.tabPanel}>
                      <UserInformation user={user} />
                    </TabPanel>
                  </TabContext>
                </Box>
              )
            )}
          </NoPaperPageContent>
        </AdministrationLayout>
      </MenuProvider>
    </>
  );
};

export default AdministrationUserShow;
